import { SHOPPING_ADS } from '@/constants/others'
import { useGetShoppingAdsQuery } from '@/generated'
import { useGetSearchCategory } from '@/hooks/category/useGetSearchCategory'
import { useGetSearchKeyword } from '@/hooks/keywords/useGetSearchKeywords'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import {
  GA_SCROLL_DIRECTION,
  getShoppingAdClickLabel,
  getShoppingAdLoadedLabel,
  getShoppingAdScrollLabel,
} from '@/lib/ga/events/shoppingAd'
import { sendToLogger } from '@/utils/sendToLogger'

import { ShoppingAdsCarousel } from './ShoppingAdsCarousel'

/**
 * Shopping Ads for SRP, handles the logic to display the shopping ads carousel
 *
 * @returns the carousel component or null
 */
export const ShoppingAds = () => {
  const { category } = useGetSearchCategory()
  const { location } = useGetLocation()
  const { keyword } = useGetSearchKeyword()

  const { data } = useGetShoppingAdsQuery({
    skip: !category.id || !location.id,
    variables: {
      categoryId: category.id,
      location: { id: location.id },
      keywords: keyword,
      pagination: { limit: SHOPPING_ADS.MAX },
    },
    onCompleted: ({ listingShoppingAds }) => {
      if (!listingShoppingAds.length) return

      trackEvent({
        action: GA_EVENT.ShoppingAdLoaded,
        label: getShoppingAdLoadedLabel({ listingsCount: listingShoppingAds.length }),
      })
    },
    onError: (err) => {
      sendToLogger(err, { fingerprint: ['ShoppingAds'] })
    },
  })

  const trackShoppingAdsClick = (id: string, pos: number) => {
    trackEvent({ action: GA_EVENT.ShoppingAdClick, label: getShoppingAdClickLabel({ id, pos }) })
  }
  const trackShoppingAdsScrollPrev = () => {
    trackEvent({
      action: GA_EVENT.ShoppingAdScroll,
      label: getShoppingAdScrollLabel({ scrollDirection: GA_SCROLL_DIRECTION.backward }),
    })
  }
  const trackShoppingAdsScrollNext = () => {
    trackEvent({
      action: GA_EVENT.ShoppingAdScroll,
      label: getShoppingAdScrollLabel({ scrollDirection: GA_SCROLL_DIRECTION.forward }),
    })
  }

  return data?.listingShoppingAds.length ? (
    <ShoppingAdsCarousel
      shoppingAdsListing={data.listingShoppingAds}
      handleShoppingAdClick={trackShoppingAdsClick}
      handleShoppingAdScrollPrev={trackShoppingAdsScrollPrev}
      handleShoppingAdScrollNext={trackShoppingAdsScrollNext}
    />
  ) : null
}
